/**
 * Lighter Credentials Manager
 * Manages Lighter-specific credentials separately from Hyperliquid
 * Credentials: API Key (encrypted), Public Address (encrypted), Account Index, API Key Index
 */

class LighterCredentialsManager {
    constructor() {
        // Storage markers
        this.apiKeyMarker = 'lighter_encrypted_api_key';
        this.publicAddressMarker = 'lighter_encrypted_address';
        this.accountIndexMarker = 'lighter_account_index';
        this.apiKeyIndexMarker = 'lighter_api_key_index';
        this.networkMarker = 'lighter_network';
        
        // In-memory values (loaded after authentication)
        this.apiKey = null;
        this.publicAddress = null;
        this.accountIndex = null;
        this.apiKeyIndex = null;
        this.network = 'mainnet'; // Default to mainnet
        
        this.isLoaded = false;
    }
    
    /**
     * Save Lighter credentials
     * @param {string} apiKey - Lighter API key
     * @param {string} publicAddress - Public address
     * @param {number} accountIndex - Lighter account index
     * @param {number} apiKeyIndex - API key index (usually 0)
     * @param {string} network - Network (mainnet/testnet)
     */
    async saveLighterCredentials(apiKey, publicAddress, accountIndex, apiKeyIndex, network = 'mainnet') {
        // Validate inputs
        if (!apiKey || !apiKey.trim()) {
            throw new Error('API key is required');
        }
        if (!publicAddress || !publicAddress.trim()) {
            throw new Error('Public address is required');
        }
        if (!Number.isInteger(accountIndex) || accountIndex < 0) {
            throw new Error('Account index must be a positive integer');
        }
        if (!Number.isInteger(apiKeyIndex) || apiKeyIndex < 0) {
            throw new Error('API key index must be a positive integer');
        }
        
        // Use master password from PasswordManager
        if (!window.PasswordManager?.isUserReady()) {
            throw new Error('Password manager not ready');
        }
        
        try {
            // Encrypt API key using master password
            const encryptedApiKey = await window.PasswordManager.encryptData(apiKey.trim());
            localStorage.setItem(this.apiKeyMarker, JSON.stringify(encryptedApiKey));
            
            // Encrypt public address using master password
            const encryptedAddress = await window.PasswordManager.encryptData(publicAddress.trim());
            localStorage.setItem(this.publicAddressMarker, JSON.stringify(encryptedAddress));
            
            // Store indices and network as plaintext
            localStorage.setItem(this.accountIndexMarker, accountIndex.toString());
            localStorage.setItem(this.apiKeyIndexMarker, apiKeyIndex.toString());
            localStorage.setItem(this.networkMarker, network);
            
            // Update in-memory values
            this.apiKey = apiKey.trim();
            this.publicAddress = publicAddress.trim();
            this.accountIndex = accountIndex;
            this.apiKeyIndex = apiKeyIndex;
            this.network = network;
            this.isLoaded = true;
            
            console.log('✅ [LIGHTER-CREDS] Credentials saved successfully');
            return true;
        } catch (error) {
            console.error('❌ [LIGHTER-CREDS] Failed to save:', error);
            throw error;
        }
    }
    
    /**
     * Load Lighter credentials (called after password authentication)
     */
    async loadLighterCredentials() {
        if (!window.PasswordManager?.isUserReady()) {
            throw new Error('Password manager not ready');
        }
        
        try {
            // Load and decrypt API key
            const encryptedApiKey = localStorage.getItem(this.apiKeyMarker);
            if (encryptedApiKey) {
                const apiKeyData = JSON.parse(encryptedApiKey);
                this.apiKey = await window.PasswordManager.decryptData(apiKeyData);
            }
            
            // Load and decrypt public address
            const encryptedAddress = localStorage.getItem(this.publicAddressMarker);
            if (encryptedAddress) {
                const addressData = JSON.parse(encryptedAddress);
                this.publicAddress = await window.PasswordManager.decryptData(addressData);
            }
            
            // Load indices and network
            const accountIndex = localStorage.getItem(this.accountIndexMarker);
            const apiKeyIndex = localStorage.getItem(this.apiKeyIndexMarker);
            const network = localStorage.getItem(this.networkMarker);
            
            this.accountIndex = accountIndex ? parseInt(accountIndex) : null;
            this.apiKeyIndex = apiKeyIndex ? parseInt(apiKeyIndex) : 0; // Default 0
            this.network = network || 'mainnet'; // Default mainnet
            
            this.isLoaded = true;
            
            // console.log('✅ [LIGHTER-CREDS] Credentials loaded');
            return {
                apiKey: this.apiKey,
                publicAddress: this.publicAddress,
                accountIndex: this.accountIndex,
                apiKeyIndex: this.apiKeyIndex,
                network: this.network
            };
        } catch (error) {
            console.error('❌ [LIGHTER-CREDS] Failed to load:', error);
            this.clearInMemory();
            throw error;
        }
    }
    
    /**
     * Get credentials for SDK initialization
     */
    getLighterCredentials() {
        if (!this.isLoaded) {
            return null;
        }
        
        return {
            apiKey: this.apiKey,
            publicAddress: this.publicAddress,
            accountIndex: this.accountIndex,
            apiKeyIndex: this.apiKeyIndex,
            network: this.network
        };
    }
    
    /**
     * Check if Lighter is configured
     */
    hasLighterCredentials() {
        return localStorage.getItem(this.apiKeyMarker) !== null &&
               localStorage.getItem(this.accountIndexMarker) !== null;
    }
    
    /**
     * Clear Lighter credentials
     */
    clearLighterCredentials() {
        localStorage.removeItem(this.apiKeyMarker);
        localStorage.removeItem(this.publicAddressMarker);
        localStorage.removeItem(this.accountIndexMarker);
        localStorage.removeItem(this.apiKeyIndexMarker);
        localStorage.removeItem(this.networkMarker);
        this.clearInMemory();
        console.log('✅ [LIGHTER-CREDS] Credentials cleared');
    }
    
    /**
     * Clear in-memory values
     */
    clearInMemory() {
        this.apiKey = null;
        this.publicAddress = null;
        this.accountIndex = null;
        this.apiKeyIndex = null;
        this.network = 'mainnet';
        this.isLoaded = false;
    }
}

// Create singleton
const lighterCredentialsManager = new LighterCredentialsManager();
window.LighterCredentialsManager = lighterCredentialsManager;

export { LighterCredentialsManager, lighterCredentialsManager as default };
